import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Container, Dialog, Fab, Link, Menu, MenuItem, Snackbar, Stack, Typography,
} from '@mui/material';
import {
  Close, ExpandMore, MapOutlined, NavigateBefore, NavigateNext, VerifiedUserOutlined, PersonSearchOutlined, GavelOutlined,
} from '@mui/icons-material';
import Seo from '../components/Seo';
import SearchHeader, { INK } from '../components/listings/SearchHeader';
import FilterRow from '../components/listings/FilterRow';
import FiltersDrawer, { countActive } from '../components/listings/FiltersDrawer';
import SaveSearchDialog from '../components/listings/SaveSearchDialog';
import SignInToSaveDialog from '../components/listings/SignInToSaveDialog';
import ListingsMap from '../components/listings/ListingsMap';
import PropertyCard from '../components/listings/PropertyCard';
import { forgetPendingSave, readPendingSave } from '../components/listings/pendingSave';
import publicListingsService from '../services/publicListingsService';
import { useAuth } from '../contexts/AuthContext';
import { brand, text } from '../theme/colors';

// Filters the API understands, in the order they appear on the pill bar.
const API_KEYS = ['realtor', 'type', 'city', 'suburb', 'min_price', 'max_price', 'min_size', 'max_size', 'beds_min', 'baths_min', 'q', 'bbox',
  'features', 'listed_within', 'hide_sold', 'include_por', 'sort', 'tenure'];
const SORTS = [
  { value: '', label: 'Newest' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'size_desc', label: 'Largest stand' },
];
const titleCase = (s) => String(s).replace(/\b([a-z])/g, (m) => m.toUpperCase());
// Floors for the numeric keys: prices and sizes can legitimately be 0, but
// there is no page 0 — a clamp to 0 there would ask the API for nothing.
const NUMERIC_FLOORS = { min_price: 0, max_price: 0, min_size: 0, max_size: 0, beds_min: 0, baths_min: 0, page: 1 };
const LIMIT = 20;

/**
 * Public browse for verified property listings: pill filters, a two-column
 * result grid and a map of price pins beside it. The URL is the single source of
 * truth for every filter, so a buyer can send a search to family back home and
 * they see exactly the same page.
 */
export default function PropertiesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [toast, setToast] = useState('');
  // Saves are optimistic: the payload's `saved` flag is the baseline and this
  // map holds the buyer's unconfirmed intent on top of it.
  const [savedOverride, setSavedOverride] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [sortAnchor, setSortAnchor] = useState(null);

  const qs = params.toString();
  const page = Math.max(parseInt(params.get('page'), 10) || 1, 1);
  const savedOnly = params.get('saved') === '1';
  const bbox = params.get('bbox') || '';
  // List, List + map (default) or Map — desktop only; phones keep the Map button.
  const view = ['list', 'map'].includes(params.get('view')) ? params.get('view') : 'split';

  const values = useMemo(() => {
    const sp = new URLSearchParams(qs);
    return Object.fromEntries([...API_KEYS, 'saved'].map((k) => [k, sp.get(k) || '']));
  }, [qs]);

  useEffect(() => {
    let live = true;
    const load = async () => {
      setLoading(true); setError('');
      try {
        if (savedOnly) {
          const res = await publicListingsService.saved();
          const items = res.data?.items || [];
          if (live) setData({ items, total: items.length, limit: items.length || LIMIT });
          return;
        }
        const sp = new URLSearchParams(qs);
        const query = { limit: LIMIT };
        API_KEYS.forEach((k) => { const v = sp.get(k); if (v) query[k] = v; });
        query.page = Math.max(parseInt(sp.get('page'), 10) || 1, 1);
        const res = await publicListingsService.list(query);
        if (live) setData(res.data);
      } catch {
        // Drop the old result too: a grid from the previous filters sitting under
        // an error banner reads as if those are the matches for the new search.
        if (live) {
          setData(null);
          setError(savedOnly
            ? 'We could not load your saved homes just now. Please try again in a moment.'
            : 'We could not load the listings just now. Please try again in a moment.');
        }
      } finally {
        if (live) setLoading(false);
      }
    };
    load();
    return () => { live = false; };
  }, [qs, savedOnly, isAuthenticated]);

  /**
   * Applies one filter (`setFilter('type', 'house')`) or several at once
   * (`setFilter({ min_price, max_price })`). The patch form matters: a caller
   * that needs two keys changed together cannot make two calls, because both
   * would be computed from the same pre-change URL and the second would drop
   * the first. Any filter change resets to page 1 — page 4 of the old result
   * set is meaningless.
   */
  const setFilter = useCallback((keyOrPatch, value) => {
    const patch = typeof keyOrPatch === 'object' && keyOrPatch !== null
      ? keyOrPatch
      : { [keyOrPatch]: value };
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([key, raw]) => {
        // The API rejects a negative or non-numeric price/size with a 400, so
        // clamp here rather than letting a stray "-" in the box blank the grid.
        let v = raw;
        if (key in NUMERIC_FLOORS && v !== '' && v != null) {
          const n = Number(v);
          v = Number.isNaN(n) ? '' : String(Math.max(NUMERIC_FLOORS[key], n));
        }
        if (v === '' || v == null) next.delete(key); else next.set(key, String(v));
        if (key !== 'page' && key !== 'view') next.delete('page');
        // Saved is a different result set entirely, not a narrowing of this one:
        // leaving a map rectangle or a price band on it would silently hide
        // homes the buyer deliberately saved.
        if (key === 'saved' && v) API_KEYS.forEach((k) => next.delete(k));
        if (key !== 'saved' && key !== 'view' && v) next.delete('saved');
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  const items = useMemo(() => (data?.items || []), [data]);
  const total = data?.total || 0;
  const limit = data?.limit || LIMIT;
  const lastPage = Math.max(Math.ceil(total / limit), 1);

  const isSaved = useCallback(
    (l) => (l.id in savedOverride ? savedOverride[l.id] : !!l.saved),
    [savedOverride],
  );

  const toggleSave = useCallback(async (id, nextSaved) => {
    setSavedOverride((s) => ({ ...s, [id]: nextSaved }));
    try {
      if (nextSaved) await publicListingsService.save(id);
      else await publicListingsService.unsave(id);
      setToast(nextSaved ? 'Saved to your homes' : 'Removed from your homes');
      // On the Saved tab an unsave should actually leave the list.
      if (!nextSaved && savedOnly) setData((d) => (d ? { ...d, items: d.items.filter((l) => l.id !== id) } : d));
    } catch (e) {
      setSavedOverride((s) => ({ ...s, [id]: !nextSaved }));
      // Say why when the server knows (e.g. a realtor saving their own listing).
      setToast(e?.response?.data?.error || 'We could not update your saved homes. Please try again.');
    }
  }, [savedOnly]);

  // Logged out: ask first (log in or create an account), then carry the home
  // across the round trip — see SignInToSaveDialog.
  const [signInFor, setSignInFor] = useState(null);
  const requireLogin = useCallback((id) => setSignInFor(id), []);

  // Finish a save the buyer asked for before signing in.
  const claimedRef = useRef(false);
  useEffect(() => {
    if (claimedRef.current || !isAuthenticated) return;
    const pending = location.state?.saveListingId || readPendingSave();
    if (!pending) return;
    claimedRef.current = true;
    forgetPendingSave();
    if (location.state?.saveListingId) navigate(location.pathname + location.search, { replace: true, state: null });
    toggleSave(pending, true);
  }, [isAuthenticated, location, navigate, toggleSave]);

  const emptyMessage = bbox
    ? 'No verified listings in this area yet.'
    : (savedOnly ? 'You have not saved any homes yet. Tap the heart on a listing to keep it here.' : 'No verified homes match this search yet. Try fewer filters, or save the search and we\'ll tell you when one is listed.');

  const place = values.q ? titleCase(values.q) : (values.city || 'Zimbabwe');
  const heading = savedOnly ? 'Your saved homes' : `Homes for sale in ${place}`;
  const countLabel = loading ? '' : (savedOnly
    ? `${items.length} saved ${items.length === 1 ? 'home' : 'homes'}`
    : `${total} verified ${total === 1 ? 'home' : 'homes'}`);
  const sortLabel = (SORTS.find((o) => o.value === (values.sort || '')) || SORTS[0]).label;

  const openSaveSearch = () => {
    if (!isAuthenticated) {
      navigate(`/register?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`, { state: { from: location } });
      return;
    }
    setSaveOpen(true);
  };
  const searchValues = Object.fromEntries(API_KEYS.filter((k) => k !== 'sort').map((k) => [k, values[k]]).filter(([, v]) => v));

  const cardProps = (l) => ({
    listing: l,
    saved: isSaved(l),
    canSave: isAuthenticated,
    onToggleSave: toggleSave,
    onRequireLogin: requireLogin,
    hovered: hoveredId === l.id,
    onHover: setHoveredId,
  });

  const map = (
    <ListingsMap
      items={items}
      hoveredId={hoveredId}
      onHover={setHoveredId}
      onSelect={(id) => navigate(`/properties/${id}`)}
      onSearchArea={savedOnly ? undefined : ((b) => setFilter('bbox', b.join(',')))}
    />
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <Seo
        title="Verified property listings in Zimbabwe"
        description="Stands and houses independently verified by Eqaya before listing: ownership checked, rates checked, a verifier on site."
        canonicalPath="/properties"
      />

      <Box sx={{ bgcolor: '#fff', mx: { xs: -2, sm: -3 }, px: { xs: 2, sm: 3 }, pt: { xs: 0.5, md: 1 }, pb: 1.5, mb: 2.5, borderBottom: '1px solid #dfe6e4' }}>
        <SearchHeader
          values={values}
          onSearch={(patch) => setFilter(patch)}
          onSaveSearch={openSaveSearch}
          view={view}
          onViewChange={(v) => setFilter('view', v === 'split' ? '' : v)}
        />
        <Box sx={{ mt: 1.5 }}>
          <FilterRow values={values} onChange={setFilter} onOpenFilters={() => setFiltersOpen(true)} activeCount={countActive(values)} />
        </Box>
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800, color: INK, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
          {heading}
        </Typography>
        <Stack direction="row" alignItems="center" sx={{ mt: 1, gap: { xs: 1.5, md: 3 }, flexWrap: 'wrap' }}>
          {countLabel && <Typography sx={{ fontWeight: 700, color: INK }}>{countLabel}</Typography>}
          {!savedOnly && (
            <>
              <Button
                onClick={(e) => setSortAnchor(e.currentTarget)} endIcon={<ExpandMore />}
                sx={{ textTransform: 'none', color: text.secondary, fontSize: 16, p: 0, minWidth: 0, '&:hover': { bgcolor: 'transparent' } }}
              >
                Sort by&nbsp;<Box component="span" sx={{ color: INK, fontWeight: 700, textDecoration: 'underline' }}>{sortLabel}</Box>
              </Button>
              <Menu anchorEl={sortAnchor} open={!!sortAnchor} onClose={() => setSortAnchor(null)}>
                {SORTS.map((o) => (
                  <MenuItem key={o.value || 'newest'} selected={o.value === (values.sort || '')} onClick={() => { setSortAnchor(null); setFilter('sort', o.value); }}>
                    {o.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {isAuthenticated && (
            <Button onClick={() => setFilter('saved', values.saved ? '' : '1')} sx={{ textTransform: 'none', fontWeight: 700, color: brand.primaryDark, p: 0, minWidth: 0 }}>
              {values.saved ? 'Back to all homes' : 'Saved homes'}
            </Button>
          )}
          <Link
            component={RouterLink} to="/buying-property"
            sx={{ ml: { md: 'auto' }, display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600, color: brand.primaryDark }}
          >
            <VerifiedUserOutlined fontSize="small" /> Buying from abroad? See what we check and how buying works
          </Link>
        </Stack>
        {!savedOnly && (
          <Typography variant="body2" sx={{ color: text.secondary, mt: 0.75 }}>
            Every home here has verified ownership papers: a title deed searched at the Deeds Office, or a cession confirmed with the council or developer.
          </Typography>
        )}
        {/* Owner, 2026-09-26: buyers should hire their realtor (and conveyancer) on Eqaya. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.75, sm: 2.5 }} alignItems={{ sm: 'center' }} useFlexGap flexWrap="wrap"
          sx={{ mt: 1.5, px: 2, py: 1.25, borderRadius: '10px', bgcolor: '#f1f5f4' }}>
          <Typography variant="body2" sx={{ color: text.primary, fontWeight: 600 }}>Get your own people on your side:</Typography>
          <Link component={RouterLink} to="/realtors" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700, color: brand.primaryDark }}>
            <PersonSearchOutlined fontSize="small" /> Find a realtor
          </Link>
          <Link component={RouterLink} to="/conveyancers" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700, color: brand.primaryDark }}>
            <GavelOutlined fontSize="small" /> Find a conveyancer
          </Link>
          <Typography variant="caption" sx={{ color: text.secondary }}>Every one is checked against the EAC or Law Society register.</Typography>
        </Stack>
      </Box>

      {bbox && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: text.secondary }}>Showing the area you drew on the map.</Typography>
          <Button size="small" onClick={() => setFilter('bbox', '')} sx={{ textTransform: 'none', color: brand.primary }}>
            Clear area
          </Button>
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Box
        sx={{
          display: 'grid', gap: 3, alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', md: view === 'split' ? '1.15fr 1fr' : '1fr' },
        }}
      >
        <Box sx={{ display: { xs: 'block', md: view === 'map' ? 'none' : 'block' } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: brand.primary }} aria-label="Loading listings" />
            </Box>
          ) : (
            <>
              {items.length === 0 && !error ? (
                <Alert severity="info">{emptyMessage}</Alert>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: view === 'list' ? '1fr 1fr 1fr' : '1fr 1fr' }, gap: 2.5 }}>
                  {items.map((l) => <PropertyCard key={l.id} {...cardProps(l)} />)}
                </Box>
              )}

              {!savedOnly && total > limit && (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 4 }}>
                  <Button startIcon={<NavigateBefore />} disabled={page <= 1} onClick={() => setFilter('page', page - 1)}>
                    Previous
                  </Button>
                  <Typography variant="body2" sx={{ color: text.secondary }}>Page {page} of {lastPage}</Typography>
                  <Button endIcon={<NavigateNext />} disabled={page >= lastPage} onClick={() => setFilter('page', page + 1)}>
                    Next
                  </Button>
                </Stack>
              )}

              <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mt: 4, maxWidth: 640 }}>
                Every property here was checked by Eqaya before it was listed: an independent deeds search (or, for a
                cession, the council or developer register), a rates check at the local authority, and a verifier on site.
              </Typography>
            </>
          )}
        </Box>

        {view !== 'list' && (
          <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 16, height: view === 'map' ? 'calc(100vh - 150px)' : 'calc(100vh - 120px)' }}>
            {map}
          </Box>
        )}
      </Box>

      <Fab
        variant="extended"
        onClick={() => setMapOpen(true)}
        sx={{
          display: { xs: 'flex', md: 'none' }, position: 'fixed', bottom: 24, left: '50%',
          transform: 'translateX(-50%)', bgcolor: brand.primary, color: '#fff', textTransform: 'none',
          '&:hover': { bgcolor: brand.primaryDark },
        }}
      >
        <MapOutlined sx={{ mr: 1 }} /> Map
      </Fab>

      <Dialog fullScreen open={mapOpen} onClose={() => setMapOpen(false)}>
        <Box sx={{ position: 'relative', height: '100%' }}>
          <ListingsMap
            items={items}
            hoveredId={hoveredId}
            onHover={setHoveredId}
            onSelect={(id) => navigate(`/properties/${id}`)}
            onSearchArea={savedOnly ? undefined : ((b) => { setFilter('bbox', b.join(',')); setMapOpen(false); })}
          />
          <Fab
            size="small"
            onClick={() => setMapOpen(false)}
            aria-label="Close map"
            sx={{ position: 'absolute', top: 12, right: 12, bgcolor: '#fff' }}
          >
            <Close />
          </Fab>
        </Box>
      </Dialog>

      <FiltersDrawer
        open={filtersOpen}
        values={values}
        baseQuery={Object.fromEntries(['q', 'city', 'suburb', 'bbox', 'sort'].map((k) => [k, values[k]]).filter(([, v]) => v))}
        onClose={() => setFiltersOpen(false)}
        onApply={(draft) => { setFiltersOpen(false); setFilter(draft); }}
      />
      <SaveSearchDialog
        open={saveOpen}
        values={searchValues}
        onClose={() => setSaveOpen(false)}
        onSaved={() => { setSaveOpen(false); setToast('Search saved. We\'ll tell you when a new home matches.'); }}
      />

      <SignInToSaveDialog listingId={signInFor} onClose={() => setSignInFor(null)} />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
}
